import { toReactNode } from '@affine/component';
import { AIChatBlockPeekViewTemplate } from '@affine/core/blocksuite/ai';
import type { AIChatBlockModel } from '@affine/core/blocksuite/ai/blocks/ai-chat-block/model/ai-chat-model';
import { PurchaseConfirmDialog } from '@affine/core/components/consent';
import { useAIChatConfig } from '@affine/core/components/hooks/affine/use-ai-chat-config';
import { useAISubscribe } from '@affine/core/components/hooks/affine/use-ai-subscribe';
import {
  AIDraftService,
  AIToolsConfigService,
} from '@affine/core/modules/ai-button';
import { AIModelService } from '@affine/core/modules/ai-button/services/models';
import { ServerService, SubscriptionService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { PeekViewService } from '@affine/core/modules/peek-view';
import type { EditorHost } from '@blocksuite/affine/std';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { useFramework } from '@toeverything/infra';
import { useEffect, useMemo, useRef } from 'react';

import * as styles from './index.css';

export type AIChatBlockPeekViewProps = {
  model: AIChatBlockModel;
  host: EditorHost;
  open: boolean;
  onRequestClose: () => void;
};

export const AIChatBlockPeekView = ({
  model,
  host,
  open,
  onRequestClose,
}: AIChatBlockPeekViewProps) => {
  const composerRef = useRef<HTMLDivElement>(null);
  const {
    docDisplayConfig,
    searchMenuConfig,
    networkSearchConfig,
    reasoningConfig,
  } = useAIChatConfig();

  const framework = useFramework();
  const serverService = framework.get(ServerService);
  const affineFeatureFlagService = framework.get(FeatureFlagService);
  const peekViewService = framework.get(PeekViewService);
  const affineWorkspaceDialogService = framework.get(WorkspaceDialogService);
  const aiDraftService = framework.get(AIDraftService);
  const aiToolsConfigService = framework.get(AIToolsConfigService);
  const subscriptionService = framework.get(SubscriptionService);
  const aiModelService = framework.get(AIModelService);

  useEffect(() => {
    const rootBlockId = host.store.root?.id;
    const toolbar = rootBlockId
      ? host.view.getWidget('edgeless-toolbar-widget', rootBlockId)
      : null;
    let syncFrame = 0;
    const syncHorizontalLayout = () => {
      cancelAnimationFrame(syncFrame);
      syncFrame = requestAnimationFrame(() => {
        const composer = composerRef.current;
        if (!composer || !toolbar) return;
        const toolbarRect = toolbar.getBoundingClientRect();
        if (toolbarRect.width <= 0) return;
        composer.style.setProperty(
          '--canvas-composer-center-x',
          `${toolbarRect.left + toolbarRect.width / 2}px`
        );
        composer.style.setProperty(
          '--canvas-composer-available-width',
          `${toolbarRect.width}px`
        );
      });
    };
    const resizeObserver = toolbar
      ? new ResizeObserver(syncHorizontalLayout)
      : null;
    if (toolbar) resizeObserver?.observe(toolbar);
    window.addEventListener('resize', syncHorizontalLayout);
    syncHorizontalLayout();
    toolbar?.toggleAttribute('data-ai-chat-composer-open', open);
    return () => {
      cancelAnimationFrame(syncFrame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncHorizontalLayout);
      toolbar?.removeAttribute('data-ai-chat-composer-open');
    };
  }, [host, open]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onRequestClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onRequestClose, open]);

  useEffect(() => {
    if (!open) return;
    let pointerStart: {
      id: number;
      x: number;
      y: number;
    } | null = null;

    const isInteractivePointer = (event: PointerEvent) =>
      event
        .composedPath()
        .some(
          target =>
            target instanceof HTMLElement &&
            target.matches(
              'button, input, textarea, select, a, [role="button"], [contenteditable="true"]'
            )
        );
    const handleCanvasPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || isInteractivePointer(event)) {
        pointerStart = null;
        return;
      }
      pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
    };
    const handleCanvasPointerUp = (event: PointerEvent) => {
      const start = pointerStart;
      pointerStart = null;
      if (!start || start.id !== event.pointerId) return;
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 5) {
        return;
      }

      const gfx = host.std.getOptional(GfxControllerIdentifier);
      if (!gfx) return;
      const [modelX, modelY] = gfx.viewport.toModelCoordFromClientCoord([
        event.clientX,
        event.clientY,
      ]);
      const hitElement = gfx.getElementByPoint(modelX, modelY);
      if (!hitElement) onRequestClose();
    };
    const handleCanvasPointerCancel = () => {
      pointerStart = null;
    };

    host.addEventListener('pointerdown', handleCanvasPointerDown, true);
    host.addEventListener('pointerup', handleCanvasPointerUp, true);
    host.addEventListener('pointercancel', handleCanvasPointerCancel, true);
    return () => {
      host.removeEventListener('pointerdown', handleCanvasPointerDown, true);
      host.removeEventListener('pointerup', handleCanvasPointerUp, true);
      host.removeEventListener(
        'pointercancel',
        handleCanvasPointerCancel,
        true
      );
    };
  }, [host, onRequestClose, open]);

  // Trigger model loading for current workspace (idempotent)
  const wsMatch = location.pathname.match(/\/workspace\/([^/]+)/);
  if (wsMatch) {
    aiModelService.loadForWorkspace(wsMatch[1]).catch(console.error);
  }
  const {
    openConsentDialog: handleAISubscribe,
    consentDialogProps: aiConsentProps,
  } = useAISubscribe();

  const templateNode = useMemo(() => {
    const template = AIChatBlockPeekViewTemplate(
      model,
      host,
      docDisplayConfig,
      searchMenuConfig,
      networkSearchConfig,
      reasoningConfig,
      serverService,
      affineFeatureFlagService,
      peekViewService,
      affineWorkspaceDialogService,
      aiDraftService,
      aiToolsConfigService,
      subscriptionService,
      aiModelService,
      handleAISubscribe
    );
    return toReactNode(template);
  }, [
    model,
    host,
    docDisplayConfig,
    searchMenuConfig,
    networkSearchConfig,
    reasoningConfig,
    serverService,
    affineFeatureFlagService,
    peekViewService,
    affineWorkspaceDialogService,
    aiDraftService,
    aiToolsConfigService,
    subscriptionService,
    aiModelService,
    handleAISubscribe,
  ]);

  return (
    <>
      <div
        ref={composerRef}
        className={styles.canvasComposer}
        data-open={open}
        data-testid="canvas-ai-chat-composer"
      >
        {templateNode}
      </div>
      <PurchaseConfirmDialog {...aiConsentProps} />
    </>
  );
};
